/**
 * UpdateAvailableBanner — tells a stale tab it is stale, and gets it current.
 *
 * iTow is an installed PWA. The browser keeps serving the shell it already has,
 * so a deploy is invisible until something forces the issue: every deploy this
 * project has shipped needed someone to be told "hard refresh", and more than
 * one bug was chased twice because the first fix was already live. The service
 * worker does update eventually — "eventually" is not good enough when the
 * stale screen belongs to a dispatcher looking at a job that no longer exists.
 *
 * Mounted at the router root rather than inside AppShell on purpose: a stale
 * *login* page is the worst case, because a client running an old bundle
 * against a changed contract cannot sign in far enough to be told anything.
 *
 * A prompt, not a forced reload — someone half-way through a claim form should
 * not have the page pulled out from under them. `forced` is the exception, and
 * exists for a build that is actively broken.
 */
import { useEffect, useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppUpdate, reloadToLatestBuild } from '@/core/version';

export default function UpdateAvailableBanner() {
  const { t } = useTranslation();
  const { updateAvailable, message, forced } = useAppUpdate();
  const [dismissed, setDismissed] = useState(false);
  const [reloading, setReloading] = useState(false);

  // A forced update reloads itself. Deliberately not instant: a moment of the
  // banner being visible is the difference between "the app updated" and "the
  // app crashed", and that moment costs nothing.
  useEffect(() => {
    if (!forced || reloading) return;
    setReloading(true);
    const timer = setTimeout(() => void reloadToLatestBuild(), 2_000);
    return () => clearTimeout(timer);
  }, [forced, reloading]);

  const open = updateAvailable && !dismissed;

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      // No autoHideDuration: an update notice that disappears on its own is one
      // nobody acts on.
      sx={{ zIndex: (theme) => theme.zIndex.snackbar + 1 }}
    >
      <Alert
        severity="info"
        // A forced reload is already under way — offering a dismiss would be a
        // button that does nothing.
        onClose={forced ? undefined : () => setDismissed(true)}
        action={
          <Button
            color="inherit"
            size="small"
            disabled={reloading}
            onClick={() => {
              setReloading(true);
              void reloadToLatestBuild();
            }}
          >
            {t('UPDATE.reload')}
          </Button>
        }
      >
        <strong>{t('UPDATE.title')}</strong>
        <br />
        {message ?? t(forced ? 'UPDATE.forcedBody' : 'UPDATE.body')}
      </Alert>
    </Snackbar>
  );
}
